import React, { useRef, useState } from "react";
import { alpha, Box, Fade, IconButton, useTheme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { StyledInputBase } from "../styled/inputs/searchInput";

const SearchBar = ({
  searchQuery = "",
  onSearchChange,
  onClear,
  placeholder = "Pesquisar...",
  size = "medium",
}) => {
  const theme = useTheme();
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  const handleInputChange = (e) => {
    onSearchChange?.(e);
  };

  const handleClearClick = () => {
    onClear?.();
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const getContainerStyles = () => {
    const isSmallSize = size === "small";

    return {
      position: "relative",
      display: "flex",
      alignItems: "center",
      backgroundColor:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.common.white, 0.08)
          : alpha(theme.palette.grey[500], 0.06),
      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${
        isFocused
          ? theme.palette.primary.main
          : theme.palette.mode === "dark"
          ? alpha(theme.palette.common.white, 0.12)
          : alpha(theme.palette.grey[500], 0.2)
      }`,
      transition: theme.transitions.create(
        ["border-color", "background-color", "box-shadow"],
        {
          duration: theme.transitions.duration.short,
        }
      ),
      minHeight: isSmallSize ? 40 : 48,
      maxWidth: { xs: "50%", sm: "100%" },

      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.common.white, 0.12)
            : alpha(theme.palette.grey[500], 0.08),
        borderColor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.common.white, 0.2)
            : alpha(theme.palette.grey[500], 0.3),
      },

      ...(isFocused && {
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`,
        backgroundColor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.common.white, 0.1)
            : theme.palette.background.paper,
      }),
    };
  };

  const getSearchIconStyles = () => ({
    position: "absolute",
    left: theme.spacing(1.5),
    zIndex: 1,
    color: isFocused
      ? theme.palette.primary.main
      : theme.palette.text.secondary,
    transition: theme.transitions.create("color"),
    pointerEvents: "none",
  });

  const getClearButtonStyles = () => ({
    position: "absolute",
    right: theme.spacing(0.5),
    padding: theme.spacing(0.75),
    color: theme.palette.text.secondary,
    transition: theme.transitions.create(["color", "transform"]),

    "&:hover": {
      color: theme.palette.text.primary,
      backgroundColor: alpha(theme.palette.error.main, 0.08),
      transform: "scale(1.1)",
    },

    "&:active": {
      transform: "scale(0.95)",
    },
  });

  return (
    <Box className="search-orders-step" sx={getContainerStyles()}>
      {/* Search Icon */}
      <SearchIcon sx={getSearchIconStyles()} />

      {/* Input Field */}
      <StyledInputBase
        ref={inputRef}
        placeholder={placeholder}
        inputProps={{
          "aria-label": "search",
          "aria-describedby": "search-helper-text",
        }}
        value={searchQuery}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />

      {/* Clear Button */}
      <Fade in={Boolean(searchQuery)} timeout={200}>
        <IconButton
          onClick={handleClearClick}
          aria-label="clear search"
          size="small"
          sx={getClearButtonStyles()}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      </Fade>

      {/* Screen reader helper text */}
      <Box
        id="search-helper-text"
        sx={{
          position: "absolute",
          left: -10000,
          width: 1,
          height: 1,
          overflow: "hidden",
        }}
      >
        {searchQuery ? `Buscando...` : "Digite algo..."}
      </Box>
    </Box>
  );
};

export default SearchBar;
